import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  FileText, Upload, Sparkles, Lock, ShieldCheck, ScrollText,
  FileSignature, ClipboardList, Mic, Trash2, Send, CalendarDays,
} from "lucide-react";
import AISaintWordmark from "@/components/AISaintWordmark";

const CATEGORIES = [
  { id: "governing_docs",   label: "Governing Docs", icon: ScrollText,     desc: "CC&Rs, Bylaws, Rules" },
  { id: "meeting_minutes",  label: "Meeting Minutes", icon: ClipboardList, desc: "Board & annual meetings" },
  { id: "insurance",        label: "Insurance",       icon: ShieldCheck,   desc: "Master + D&O policies" },
  { id: "forms",            label: "Forms",           icon: FileSignature, desc: "ARC · pool · move-in" },
  { id: "transcripts",      label: "Transcripts",     icon: Mic,           desc: "Zoom / call recordings" },
];

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve({ base64: r.result.split(",")[1], filename: file.name, mime: file.type });
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function Documents() {
  const { user } = useAuth();
  const isBoard = user?.role === "admin" || user?.role === "board";
  const [category, setCategory] = useState("governing_docs");
  const [docs, setDocs] = useState([]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [form, setForm] = useState({
    category: "governing_docs", title: "", effective_date: "", notes: "", agenda: "", tags: "",
  });
  const [fileData, setFileData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);
  const [plan, setPlan] = useState("standard");
  const [aiOpen, setAiOpen] = useState(false);
  const [aiQuestion, setAiQuestion] = useState("");
  const [aiAnswer, setAiAnswer] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [d, s] = await Promise.all([
        api.get(`/documents?category=${category}`),
        api.get("/subscription").catch(() => ({ data: { plan_id: "standard" } })),
      ]);
      setDocs(d.data);
      setPlan(s.data.plan_id || "standard");
    } catch (err) {
      console.error("docs load failed:", err);
      toast.error("Could not load documents");
    }
  }, [category]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean) };
      if (fileData) { payload.file_base64 = fileData.base64; payload.file_name = fileData.filename; payload.mime_type = fileData.mime; }
      await api.post("/documents", payload);
      toast.success("Document uploaded");
      setForm({ category, title: "", effective_date: "", notes: "", agenda: "", tags: "" });
      setFileData(null); setUploadOpen(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setSaving(false); }
  };

  const removeDoc = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    try { await api.delete(`/documents/${id}`); toast.success("Deleted"); await load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not delete"); }
  };

  const askAI = async (e) => {
    e.preventDefault();
    setAiBusy(true); setAiAnswer(null);
    try {
      const { data } = await api.post(`/documents/${selected.id}/ask-ai`, { question: aiQuestion });
      setAiAnswer(data);
    } catch (err) {
      if (err.response?.status === 402) toast.error("AI Q&A requires the Elite plan.");
      else toast.error(err.response?.data?.detail || "AI query failed");
    } finally { setAiBusy(false); }
  };

  const seed = async () => {
    try {
      await api.post("/demo/seed-owner-story");
      toast.success("Sample docs + ledger + inquiry loaded");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not seed");
    }
  };

  const canAI = plan === "elite";
  const activeCat = CATEGORIES.find((c) => c.id === category);

  return (
    <div className="space-y-6" data-testid="documents-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-secondary text-sm font-medium mb-2">
            <FileText className="w-4 h-4" /> HOA intake
          </div>
          <h1 className="text-3xl font-semibold tracking-tight">Community documents</h1>
          <p className="text-foreground/70 mt-1 max-w-2xl">Every governing doc, meeting minute, insurance policy, and form in one place. Elite tier: ask <AISaintWordmark /> anything about any doc.</p>
        </div>
        <div className="flex gap-2">
          {isBoard && (
            <>
              <Button variant="outline" className="rounded-full" onClick={seed} data-testid="seed-docs-btn">
                <Sparkles className="w-4 h-4 mr-1.5" /> Load sample docs
              </Button>
              <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
                <DialogTrigger asChild>
                  <Button className="rounded-full bg-gold text-secondary-foreground hover:brightness-110" data-testid="upload-doc-btn">
                    <Upload className="w-4 h-4 mr-1.5" /> Upload document
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader><DialogTitle>Upload document</DialogTitle></DialogHeader>
                  <form onSubmit={submit} className="space-y-3" data-testid="doc-form">
                    <div>
                      <Label>Category</Label>
                      <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                        <SelectTrigger className="mt-1.5 h-11" data-testid="doc-category-select"><SelectValue /></SelectTrigger>
                        <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Title</Label>
                      <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5 h-11" data-testid="doc-title-input" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Effective date</Label>
                        <Input type="date" value={form.effective_date} onChange={(e) => setForm({ ...form, effective_date: e.target.value })} className="mt-1.5 h-11" data-testid="doc-date-input" />
                      </div>
                      <div>
                        <Label>Tags (comma-sep)</Label>
                        <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="assessments, elections" className="mt-1.5 h-11" data-testid="doc-tags-input" />
                      </div>
                    </div>
                    <div>
                      <Label>Notes</Label>
                      <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5 min-h-[70px]" data-testid="doc-notes-input" />
                    </div>
                    {form.category === "meeting_minutes" && (
                      <div>
                        <Label>Agenda</Label>
                        <Textarea value={form.agenda} onChange={(e) => setForm({ ...form, agenda: e.target.value })} className="mt-1.5 min-h-[70px]" placeholder="1. Roll call…" data-testid="doc-agenda-input" />
                      </div>
                    )}
                    <div>
                      <Label>File (optional · PDF, image, DOCX)</Label>
                      <Input type="file" accept=".pdf,.doc,.docx,image/*" onChange={async (e) => {
                        const f = e.target.files?.[0]; if (!f) return;
                        if (f.size > 7_500_000) { toast.error("Max 7.5MB"); return; }
                        setFileData(await readFileAsBase64(f));
                      }} className="mt-1.5 h-11" data-testid="doc-file-input" />
                    </div>
                    <DialogFooter>
                      <Button type="submit" disabled={saving} className="rounded-full" data-testid="doc-submit-btn">
                        {saving ? "Uploading…" : "Save"}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </>
          )}
        </div>
      </div>

      <Tabs value={category} onValueChange={setCategory}>
        <TabsList className="flex-wrap h-auto" data-testid="doc-categories">
          {CATEGORIES.map((c) => (
            <TabsTrigger key={c.id} value={c.id} data-testid={`doc-cat-${c.id}`} className="gap-1.5">
              <c.icon className="w-3.5 h-3.5" /> {c.label}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value={category} className="mt-4">
          <div className="mb-3 text-sm text-muted-foreground">{activeCat?.desc}</div>
          {docs.length === 0 ? (
            <div className="hoa-card p-10 text-center" data-testid="docs-empty">
              <FileText className="w-8 h-8 mx-auto text-muted-foreground opacity-50 mb-2" />
              No documents in this category yet.
              {isBoard && <div className="mt-3"><Button variant="outline" className="rounded-full" onClick={seed}>Load sample docs</Button></div>}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {docs.map((d) => (
                <div key={d.id} className="hoa-card hoa-card-hover p-5" data-testid={`doc-card-${d.id}`}>
                  <div className="flex items-start justify-between mb-2">
                    <FileText className="w-6 h-6 text-gold" />
                    {isBoard && (
                      <button className="text-xs text-muted-foreground hover:text-destructive" onClick={() => removeDoc(d.id)} data-testid={`doc-delete-${d.id}`} aria-label="Delete">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <h3 className="font-semibold text-foreground mb-1">{d.title}</h3>
                  {d.effective_date && (
                    <div className="text-xs text-muted-foreground flex items-center gap-1 mb-2">
                      <CalendarDays className="w-3 h-3" /> {new Date(d.effective_date).toLocaleDateString()}
                    </div>
                  )}
                  {d.notes && <p className="text-xs text-foreground/70 line-clamp-3 mb-3">{d.notes}</p>}
                  <div className="flex flex-wrap gap-1 mb-3">
                    {(d.tags || []).map((t) => (
                      <span key={t} className="text-[10px] bg-accent/40 text-foreground/70 px-2 py-0.5 rounded-full">{t}</span>
                    ))}
                  </div>
                  <Button
                    variant={canAI ? "default" : "outline"}
                    size="sm"
                    className="rounded-full w-full"
                    onClick={() => { setSelected(d); setAiOpen(true); setAiAnswer(null); setAiQuestion(""); }}
                    data-testid={`doc-ask-ai-${d.id}`}
                  >
                    {canAI ? <><Sparkles className="w-3.5 h-3.5 mr-1" /> Ask <AISaintWordmark /></> : <><Lock className="w-3.5 h-3.5 mr-1" /> AI Q&A · Elite</>}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {category === "transcripts" && (
        <div className="hoa-card royal-ring p-6" data-testid="transcription-banner">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-gold text-secondary-foreground flex items-center justify-center flex-shrink-0">
              <Mic className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold">Auto-transcription add-on</h3>
              <p className="text-sm text-foreground/75 mt-1">Upload audio, video, or connect Zoom — AI Saint transcribes every meeting into searchable minutes with action items highlighted.</p>
              <div className="flex items-center gap-2 mt-3 text-xs text-muted-foreground">
                <span className="w-2 h-2 rounded-full bg-gold animate-pulse" /> Coming with Elite · $89/mo add-on
              </div>
            </div>
            <Link to="/subscription"><Button variant="outline" className="rounded-full" data-testid="transcription-upgrade-btn">Learn more</Button></Link>
          </div>
        </div>
      )}

      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent className="max-w-lg" data-testid="doc-ai-dialog">
          <DialogHeader><DialogTitle>Ask <AISaintWordmark /> about &ldquo;{selected?.title}&rdquo;</DialogTitle></DialogHeader>
          {!canAI ? (
            <div className="p-4 text-center">
              <Lock className="w-8 h-8 text-gold mx-auto mb-3" />
              <p className="text-sm text-foreground/80 mb-3">AI Q&amp;A on your governing docs is an <span className="text-gold font-semibold">Elite</span> capability.</p>
              <Link to="/subscription"><Button className="rounded-full bg-gold text-secondary-foreground hover:brightness-110" data-testid="upgrade-ai-doc-btn">Upgrade to Elite</Button></Link>
            </div>
          ) : (
            <form onSubmit={askAI} className="space-y-3" data-testid="doc-ai-form">
              <Textarea required minLength={5} value={aiQuestion} onChange={(e) => setAiQuestion(e.target.value)} placeholder="e.g. What's the collections escalation timeline in Article VI?" className="min-h-[90px]" data-testid="doc-ai-question-input" />
              {aiAnswer && (
                <div className="p-3 rounded-lg bg-gold/10 border border-gold/30 text-sm" data-testid="doc-ai-answer">
                  <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-gold font-semibold mb-1">
                    <Sparkles className="w-3.5 h-3.5" /> AI Saint
                  </div>
                  <p className="text-foreground/85 whitespace-pre-wrap">{aiAnswer.answer}</p>
                </div>
              )}
              <DialogFooter>
                <Button type="submit" disabled={aiBusy} className="rounded-full bg-gold text-secondary-foreground hover:brightness-110" data-testid="doc-ai-submit-btn">
                  <Send className="w-3.5 h-3.5 mr-1.5" /> {aiBusy ? "Thinking…" : "Ask"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
